import { useState, MouseEvent, useCallback, Children } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { PopoverOrigin, SxProps } from '@mui/material';
import { RaRecord, useRecordContext } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { IconButton } from 'shared/mui/IconButtons';

import { ContextMenuContextProvider } from './context/contextMenuContext';
import { MenuStyled } from './ContextMenu.styled';

export type ContextMenuProps<RecordType extends RaRecord> = {
  render?: (record: RecordType, source?: string) => JSX.Element | (JSX.Element | false)[];
  source?: string;
  children?: React.ReactNode;
  sx?: SxProps;
  transformOrigin?: PopoverOrigin;
};

export function ContextMenu<RecordType extends RaRecord>({
  children,
  render,
  sx,
  transformOrigin,
}: ContextMenuProps<RecordType>) {
  const record = useRecordContext<RecordType>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const onCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const hasChildren = Boolean(Children.toArray(children).length);

  if (!hasChildren && !render) {
    return null;
  }

  return (
    <ContextMenuContextProvider value={{ onCloseMenu }}>
      <Box sx={sx}>
        <IconButton onClick={handleClick}>
          <MoreHorizIcon color="secondary" />
        </IconButton>
        <MenuStyled
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          onClose={onCloseMenu}
          open={open}
          transformOrigin={
            transformOrigin
              ? transformOrigin
              : {
                  vertical: 'top',
                  horizontal: 'right',
                }
          }
        >
          {render && record ? render(record) : children}
        </MenuStyled>
      </Box>
    </ContextMenuContextProvider>
  );
}
